"use client";

import React, { useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
function StrategyFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="w-full max-w-2xl mx-auto p-8 bg-primary/10 border border-primary/20 rounded-xl text-center animate-in fade-in zoom-in duration-300">
        <h3 className="text-2xl font-bold text-primary mb-2">Request Received!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="min-w-[150px]">
          Start New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full max-w-4xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="bg-white text-black border-0 h-12 px-4 shadow-inner placeholder:text-gray-400 focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="Email Address" className="bg-white text-black border-0 h-12 px-4 shadow-inner placeholder:text-gray-400 focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="Phone Number (Optional)" className="bg-white text-black border-0 h-12 px-4 shadow-inner placeholder:text-gray-400 focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="sr-only">Company / Organization</FormLabel>
              <FormControl>
                <Input placeholder="Company / Organization (Optional)" className="bg-white text-black border-0 h-12 px-4 shadow-inner placeholder:text-gray-400 focus-visible:ring-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="md:col-span-2">
          <FormField control={control} name="goals" render={({
          field
        }) => <FormItem>
                <FormLabel className="sr-only">Strategic Goals</FormLabel>
                <FormControl>
                  <textarea placeholder="Address of the propery to inspect..." className="flex min-h-[100px] w-full rounded-md border-0 bg-white px-4 py-3 text-base text-black shadow-inner placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {submitError && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
          {submitError}
        </div>}

      <div className="flex justify-center">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] h-14 text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </> : <>
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function ElevateYourStrategy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="elevate-your-strategy" className="dark bg-background text-foreground py-32 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none opacity-20">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-[120px]" />
      </div>

      <div ref={ref} className={cn("container mx-auto px-4 relative z-10 transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            How I Can <span className="text-primary">Help You</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Ready to take Book Your Home Inspection? Fill out the form below to schedule a strategic consultation tailored to your unique goals.
          </p>
        </div>

        <div className="bg-card/5 backdrop-blur-sm border border-white/5 rounded-2xl p-6 md:p-10 shadow-2xl">
          <FormProvider formId="strategy_form_home" sectionName="elevate-your-strategy" defaultValues={{
          name: "",
          email: "",
          phone: "",
          goals: ""
        }} validationRules={{
          name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          company: {
            required: false
          },
          goals: {
            required: false
          }
        }} submitText="Get Started" successMessage="Thank you! Your strategy request has been received. We'll be in touch shortly.">
            <StrategyFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}